import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'
import { toast } from 'sonner'

import { ApiError } from '@/api/errors'
import { queryKeys } from '@/api/keys'
import { meetingPollsApi } from '@/api/meeting-polls'
import { useCurrentUser } from '@/auth/use-auth'
import { useShellClient } from '@/components/clients/shell-client'
import { Frame } from '@/components/frame'
import { MeetingPollEditor } from '@/components/meetings/meeting-poll-editor'
import { ReturnLink } from '@/components/return-link'
import { Button } from '@/components/ui/button'
import { draftOf, toMeetingPollInput, type MeetingPollDraft } from '@/lib/meeting-poll-draft'
import { RETURN_SHAPE } from '@/lib/return-to'
import { matching, readSearch } from '@/lib/search-params'

export const Route = createFileRoute('/_app/clients/$clientId/rendez-vous_/nouvelle')({
  validateSearch: (search: Record<string, unknown>) =>
    readSearch(
      {
        /** Le projet d'où l'on vient, rattaché à la demande. */ projet: matching(/^[0-9a-f]{24}$/),
        ...RETURN_SHAPE,
      },
      search,
    ),
  component: NewMeetingPollPage,
})

/**
 * Une nouvelle demande de rendez-vous : on la rédige, elle naît brouillon, et
 * s'ouvre sur sa fiche, d'où elle s'envoie.
 */
function NewMeetingPollPage() {
  const client = useShellClient()
  const viewer = useCurrentUser()
  const { projet } = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const create = useMutation({
    mutationFn: (draft: MeetingPollDraft) =>
      meetingPollsApi.create(client.id, toMeetingPollInput(draft, viewer.timezone, projet ?? null)),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.meetingPolls.all })
      toast.success('Brouillon créé : relisez-le, puis envoyez-le.')
      void navigate({ to: '/meeting-polls/$shortId', params: { shortId: created.short_id } })
    },
  })
  return (
    <Frame>
      {/* #401 : la page d'où l'on vient (un projet, les rendez-vous du client). */}
      <ReturnLink
        names={{ client: client.name }}
        className="-ml-2"
        fallback={
          <Button asChild variant="ghost" size="sm" className="-ml-2">
            <Link to="/clients/$clientId/rendez-vous" params={{ clientId: client.id }}>
              <ArrowLeftIcon />
              Retour aux rendez-vous
            </Link>
          </Button>
        }
      />
      <h2 className="text-lg font-semibold">Nouvelle demande de rendez-vous</h2>
      <MeetingPollEditor
        clientId={client.id}
        initial={draftOf(null, viewer.id)}
        timeZone={viewer.timezone}
        saving={create.isPending}
        error={
          create.error
            ? create.error instanceof ApiError
              ? create.error.message
              : 'Création impossible.'
            : null
        }
        saveLabel="Créer le brouillon"
        requireChange={false}
        onSave={(draft) => {
          create.mutate(draft)
        }}
      />
    </Frame>
  )
}
